import { CalendarCheckIcon, CalendarXIcon, ClipboardCheckIcon, FolderSearchIcon, HammerIcon, LayoutGridIcon, PencilRulerIcon, ScissorsIcon, ShieldCheckIcon, ShuffleIcon, BookOpenTextIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// =====================================================================
// SLÖJDKLAR™ – all landing content (Swedish). Source of truth: slojdklar/PRODUCTO.md
// Images: WebP in Impultienda Recursos (store slojdklar 534e5a66…).
// =====================================================================

// Mockup: box with the 6 volymer (Higgsfield gpt_image_2_5 + tag fix with Pillow).
export const MOCKUP_URL = 'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/534e5a66-8d0b-4b1a-b1f8-3b27cfbc3c0e/1792770540100_sl-mockup-caja-6-volymer-79b62943.webp';

// "Titta inuti": real pages of Volym 1 (Gamma Estudio), same order as pageTexts: s11, s14, s22, s28, s32, s39.
export const PAGE_URLS: string[] = [
  'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/534e5a66-8d0b-4b1a-b1f8-3b27cfbc3c0e/1792770544030_sl-1-sakerhet-s11-c4b2658a.webp',
  'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/534e5a66-8d0b-4b1a-b1f8-3b27cfbc3c0e/1792770553431_sl-2-lektion2-s14-9ddafde3.webp',
  'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/534e5a66-8d0b-4b1a-b1f8-3b27cfbc3c0e/1792770556955_sl-3-lektion6-s22-01139e63.webp',
  'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/534e5a66-8d0b-4b1a-b1f8-3b27cfbc3c0e/1792770561891_sl-4-lektion9-s28-e2e5e9a3.webp',
  'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/534e5a66-8d0b-4b1a-b1f8-3b27cfbc3c0e/1792770566959_sl-5-lektion11-s32-b2fbd3ea.webp',
  'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/534e5a66-8d0b-4b1a-b1f8-3b27cfbc3c0e/1792770579671_sl-6-bedomningsmatris-s39-9fb49951.webp'
];

// ---------- Images ----------
export const PRODUCT_IMAGE = MOCKUP_URL;
export const PRODUCT_IMAGE_ALT = 'SLÖJDKLAR-paketet: sex volymer i slöjd för åk 1–9 med läsårsplan, lektioner, projekt i textil, trä och metall, säkerhet, slöjdprocessen och bedömning';

// ---------- Prices (Market Sverige, SEK) ----------
export const PRICE = '299 kr';
export const ORIGINAL_PRICE = '601 kr';

// ---------- Meta ----------
export const META = {
  title: 'SLÖJDKLAR™ – Färdiga slöjdlektioner för åk 1–9',
  description: 'Färdigt 6-i-1-system för slöjd åk 1–9: läsårsplan, 12 lektioner minut för minut, 18 projekt i textil, trä och metall, verktygskörkort och säkerhet, slöjdprocessen, bedömning och 5 bonusar.',
  ogTitle: 'SLÖJDKLAR™ – Gå in i slöjdsalen med allt klart',
  ogDescription: 'Hela läsåret i slöjd – färdigplanerat. Lektioner, projekt steg för steg i textil, trä och metall, säkerhet och bedömning för åk 1–9. Omedelbar nedladdning.'
};

// ---------- Top bar (no price, no countdown) ----------
export const topBar = {
  long: 'Hela läsåret i slöjd – färdigplanerat · Omedelbar nedladdning · 7 dagars nöjdhetsgaranti',
  short: 'Hela läsåret i slöjd · 7 dagars garanti'
};

// ---------- Section copy ----------
export const copy = {
  ctaDefault: 'Skaffa SLÖJDKLAR™ nu',
  hero: {
    before: 'Få',
    highlight: 'hela läsåret',
    middle: 'i slöjd färdigplanerat – kom igång på',
    accent: '5 minuter',
    sub: 'Gå in i slöjdsalen med allt klart. Sex digitala volymer för åk 1–9 med färdiga lektioner, 18 projekt steg för steg i textil, trä och metall, verktygskörkort och säkerhet, slöjdprocessen och bedömning – plus fem bonusar.',
    badgeLine1: '6 volymer',
    badgeLine2: '+ 5 bonusar'
  },
  stats: { title: 'Det kompletta paketet i siffror' },
  difference: {
    kicker: 'Skillnaden med SLÖJDKLAR™',
    titleStart: 'Från kaos i slöjdsalen till',
    titleAccent: 'lugn och struktur.',
    intro: 'Tänk dig att börja veckan med slöjdlektionerna redan planerade. Du vet vilket projekt varje grupp gör – och du har materiallistan, måtten, stegen, säkerhetsreglerna och bedömningen klara.',
    beforeLabel: 'Innan SLÖJDKLAR™',
    beforeTitle: 'Känner du igen dig?',
    afterLabel: 'Med SLÖJDKLAR™',
    afterTitle: 'Planera mindre. Slöjda mer.',
    closing: 'Fokusera på eleverna och händerna i arbete – inte på att skapa allt material själv.'
  },
  recognize: {
    title: 'Har du någonsin upplevt det här?',
    text: 'Om du svarar ja på minst en fråga är SLÖJDKLAR™ för dig.',
    link: 'Du förtjänar ett enklare sätt'
  },
  inside: {
    title: 'Vad innehåller',
    brand: 'SLÖJDKLAR™',
    packTitle: 'Vad gör SLÖJDKLAR™ annorlunda?',
    packText: 'Ett helt läsårs material i slöjd – textilslöjd och trä- och metallslöjd – organiserat och redo att använda.'
  },
  pages: {
    kicker: 'Titta inuti SLÖJDKLAR™',
    titleStart: 'Se hur materialet ser ut',
    titleAccent: 'på insidan.',
    text: 'Riktiga sidor ur Volym 1: säkerhetsregler, lärarsidor minut för minut med mål, material och mått, och bedömningsmatris – skapade för slöjdsalen.'
  },
  bonuses: {
    kicker: 'Men det finns mer!',
    titleStart: '5 bonusar',
    titleAccent: 'utan extra kostnad',
    text: 'Fem extra resurser ingår tillsammans med dina sex volymer – så att du kan organisera och genomföra slöjdundervisningen ännu enklare.'
  },
  how: {
    titleStart: 'Så fungerar',
    brand: 'SLÖJDKLAR™',
    text: 'Från nedladdning till slöjdsalen: förbered nästa slöjdlektion på bara fem minuter.',
    closing: 'Öppna → välj → förbered → slöjda. Enkelt och strukturerat.'
  },
  audience: {
    titleStart: 'För vem är',
    brand: 'SLÖJDKLAR™',
    lead: 'Paketet är för dig som:',
    closing: 'Skapat för slöjdlärare som vill lägga mindre tid på förberedelser och mer tid på eleverna.',
    asideTitle: 'Vad är din tid värd?',
    asideLead: 'Tänk på hur många timmar du lägger på planering i slöjd varje vecka:',
    asideText: 'Tänk dig nu ett organiserat paket som hjälper dig med allt detta – under hela läsåret.',
    asideClosing: 'Du behöver inte fortsätta börja från noll.'
  },
  offer: {
    titleStart: 'Upptäck ditt kompletta',
    brand: 'SLÖJDKLAR™',
    titleEnd: '-paket',
    text: 'Hela läsåret i slöjd – färdigplanerat. Kom igång på 5 minuter.',
    includesLabel: 'Det kompletta paketet innehåller',
    priceLabel: 'Introduktionspris',
    originalLabel: 'Ord. pris',
    oneTime: 'Engångsbetalning. Ingen prenumeration.',
    cta: 'Skaffa hela paketet nu'
  },
  guarantee: {
    title: '7 dagars nöjdhetsgaranti',
    text: 'Ladda ner materialet och se hur det fungerar i din slöjdundervisning. Om paketet inte motsvarar dina förväntningar kan du kontakta oss via e-post och begära återbetalning inom 7 dagar från köpet, enligt våra garantivillkor.',
    legal: 'Garantin påverkar inte dina rättigheter enligt gällande konsumentlagstiftning.'
  },
  finalCta: {
    titleStart: 'Din slöjdundervisning kan vara',
    titleAccent: 'redo redan idag.',
    text: 'I stället för att leta projekt, räkna ut mått och materialåtgång och skriva säkerhetsregler på kvällarna har du ett organiserat paket i slöjd för hela läsåret. Mer tid till det som verkligen betyder något:',
    closing: 'Dina elever. Din undervisning. Ditt liv utanför skolan.',
    recap: ['6 volymer', '5 bonusar', '240 sidor', '18 projekt', 'Åk 1–9'],
    oneTime: 'Engångsbetalning – ingen prenumeration.',
    reassurances: ['Omedelbar digital nedladdning', 'Utskrivbara PDF-filer', '7 dagars nöjdhetsgaranti']
  },
  footer: {
    brand: 'SLÖJDKLAR',
    disclaimer: 'SLÖJDKLAR™ är ett fristående undervisningsmaterial i slöjd för åk 1–9. Materialet bygger på det centrala innehållet i Lgr22. Följ alltid skolans egna säkerhetsrutiner i slöjdsalen.',
    copyright: '© 2026 SLÖJDKLAR™ · edulabs. Alla rättigheter förbehållna.'
  }
};

// ---------- Data ----------
export const heroGuarantees = ['Omedelbar digital nedladdning', 'Engångsbetalning', 'Ingen prenumeration', '7 dagars nöjdhetsgaranti'];

export const stats = [{
  value: '240',
  label: 'Sidor i sex volymer'
}, {
  value: '12',
  label: 'Lektioner minut för minut'
}, {
  value: '18',
  label: 'Projekt i textil, trä och metall'
}, {
  value: '12',
  label: 'Verktygskörkort'
}, {
  value: '5',
  label: 'Bonusar som ingår'
}];

export const differenceStats = [{
  value: 'Åk 1–9',
  label: 'Läsårsplan för åk 1–3, 4–6 och 7–9'
}, {
  value: '6 digitala volymer',
  label: 'Lektioner, textilslöjd, trä- och metallslöjd, säkerhet, slöjdprocessen och bedömning'
}, {
  value: 'Utskrivbara PDF-filer',
  label: 'Att skriva ut till slöjdsalen eller visa digitalt'
}];

export const beforeItems: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Söndagskvällen går åt till planering',
  text: 'Du letar projekt, räknar ut mått och materialåtgång – och veckan har inte ens börjat.'
}, {
  title: 'Trettio elever, trettio olika tempon',
  text: 'Några är klara på tio minuter, andra har knappt börjat – och alla behöver dig samtidigt.'
}, {
  title: 'Säkerheten ligger alltid i bakhuvudet',
  text: 'Kniv, såg, borr och symaskin – du vill ha tydliga regler och körkort, men har aldrig hunnit skriva ner dem.'
}, {
  title: 'Projekten blir ”samma som förra året”',
  text: 'Det finns ingen tid att hitta nya projekt med rätt progression från åk 1 till åk 9.'
}, {
  title: 'Slöjdprocessen ska dokumenteras',
  text: 'Idé, skiss, planering, framställning och värdering – men eleverna har ingen loggbok och du ingen mall.'
}, {
  title: 'Bedömningen tar kvällarna',
  text: 'Matriser, omdömen och betyg E/C/A i åk 6 och 9 blir ännu mer arbete efter skoltid.'
}];

export const afterItems = ['Ett organiserat paket i slöjd för hela grundskolan', 'Läsårsplan för åk 1–3, 4–6 och 7–9', '12 lektioner planerade minut för minut', '18 färdiga projekt med material, mått och steg för steg', 'Verktygskörkort för 12 verktyg och maskiner', 'Loggbok och processdagbok med mallar', 'Bedömningsmatriser och stöd för E/C/A', '100 färdiga kommentarer till omdömen'];

export const recognizeItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: CalendarXIcon,
  title: 'Kvällarna går åt till planering',
  text: 'Sitter du på kvällar och helger och planerar nästa veckas slöjdlektioner?'
}, {
  icon: FolderSearchIcon,
  title: 'Du letar efter projekt som fungerar',
  text: 'Söker du ständigt efter projekt med rätt svårighetsgrad, material och mått för just din årskurs?'
}, {
  icon: ShieldCheckIcon,
  title: 'Säkerheten måste sitta',
  text: 'Vill du ha tydliga säkerhetsregler och verktygskörkort som eleverna faktiskt följer?'
}, {
  icon: PencilRulerIcon,
  title: 'Du vill slippa skapa allt från noll',
  text: 'Önskar du att lektioner, arbetsritningar, elevblad och matriser redan var förberedda?'
}, {
  icon: ShuffleIcon,
  title: 'Eleverna arbetar i olika tempo',
  text: 'Behöver du uppgifter i tre nivåer – för elever som behöver stöd och för dem som vill ha mer utmaning?'
}, {
  icon: LayoutGridIcon,
  title: 'Textil, trä och metall i samma system',
  text: 'Vill du ha båda materialområdena samlade så att du snabbt hittar det du behöver?'
}];

// Spine colours mirror the box mockup (marinblå, laxrosa, trä, grön, svensk blå, röd).
export const volumes: Array<{
  number: string;
  level: string;
  title: string;
  tagline: string;
  intro: string;
  items: string[];
  outro: string;
  note?: string;
  spine: string;
  rule: string;
}> = [{
  number: 'Volym 01',
  level: 'Läsårsplan',
  title: 'Läsårsplan + 12 lektioner minut för minut',
  tagline: 'Gå in i slöjdsalen med allt klart.',
  intro: 'Den första volymen ger dig strukturen för hela läsåret och tolv färdiga lektioner på 60 minuter i textilslöjd och trä- och metallslöjd – med lärarsida och elevblad i tre nivåer.',
  items: ['Läsårsplan för åk 1–3, 4–6 och 7–9', 'Terminsplan vecka för vecka', 'Rotationsschema textil / trä och metall', 'Progressionstabell: material, redskap och tekniker', '12 lektioner minut för minut (60 min)', 'Lärarsida med MÅL, MATERIAL och SÄKERHET', 'Elevblad i 3 nivåer', 'Quiz med facit och bedömningsmatris'],
  outro: 'Från ”Välkommen till slöjdsalen” i åk 1 till ”Återbruk: från jeans till väska” i åk 7–9.',
  spine: 'bg-[#1B2A5E] text-white',
  rule: 'border-white/30'
}, {
  number: 'Volym 02',
  level: 'Textil',
  title: 'Textilslöjd: 9 projekt steg för steg',
  tagline: 'Ett projekt per årskurs – från armband till eget designprojekt.',
  intro: 'Den andra volymen innehåller nio färdiga textilprojekt med materiallista, mått, tidsplan, steg-för-steg, vanliga fel och tre nivåer.',
  items: ['Åk 1: flätat armband och tofsar', 'Åk 2: nålbok med handstygn', 'Åk 3: vävt bokmärke på pappvävram', 'Åk 4: kudde på symaskin', 'Åk 5: tygpåse med schablontryck', 'Åk 6: virkad eller stickad mössa', 'Åk 7: återbruk – jeans till väska', 'Åk 8: mjukisbyxor efter mönster', 'Åk 9: eget designprojekt i textil', 'Materiallära, stygnbank och symaskinens delar'],
  outro: 'Tydliga steg som gör att alla elever kommer i mål.',
  spine: 'bg-[#FF8A7A] text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 03',
  level: 'Trä & metall',
  title: 'Trä- och metallslöjd: 9 projekt steg för steg',
  tagline: 'Såga, fila, borra, tälja och nita – säkert och steg för steg.',
  intro: 'Den tredje volymen innehåller nio färdiga projekt i trä och metall med samma struktur: material, mått, tidsplan, steg-för-steg, vanliga fel och tre nivåer.',
  items: ['Åk 1: spikbild eller enkel träfigur', 'Åk 2: träbåt i furu', 'Åk 3: nyckelring och smyckesbricka', 'Åk 4: smörkniv (täljning)', 'Åk 5: ljusstake i plåt', 'Åk 6: skärbräda med handtag', 'Åk 7: fågelholk', 'Åk 8: smycke i ståltråd och mässing', 'Åk 9: eget designprojekt med fogar', 'Materiallära: träslag, fiberriktning, metaller och ytbehandling'],
  outro: 'Riktiga föremål som eleverna är stolta över att ta med hem.',
  spine: 'bg-[#C8894B] text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 04',
  level: 'Säkerhet',
  title: 'Säkerhet och verktyg',
  tagline: 'Trygghet i slöjdsalen – för dig och eleverna.',
  intro: 'Den fjärde volymen samlar allt du behöver för en säker slöjdsal: verktygskörkort, riskbedömning och tydliga rutiner.',
  items: ['Verktygskörkort för 12 verktyg och maskiner', 'Kniv, sågar, fil och rasp, handborr och pelarborr', 'Hammare, plåtsax, symaskin, strykjärn, limpistol och lödkolv', 'Riskbedömning per moment', 'Skyddsutrustning', '”Trafikljus” för vad eleven får göra själv', 'Ordning och städrutiner', 'Första hjälpen-rutin och olyckshantering'],
  outro: 'Skyddsglasögon på, arbetsstycket fastspänt, händerna bakom eggen.',
  spine: 'bg-[#2BB673] text-slate-900',
  rule: 'border-slate-900/20'
}, {
  number: 'Volym 05',
  level: 'Processen',
  title: 'Slöjdprocessen och design',
  tagline: 'Från idé till värdering – synligt och dokumenterat.',
  intro: 'Den femte volymen leder eleverna genom hela slöjdprocessen med loggbok, mallar och övningar i formgivning, kultur och hållbarhet.',
  items: ['Idé, inspiration och skiss', 'Arbetsritning och mått', 'Planering och framställning', 'Dokumentation och värdering', 'Loggbok och processdagbok med mallar', 'Formgivning: form, färg och funktion', 'Slöjd i kulturen och svenska slöjdtraditioner', 'Hållbarhet: återbruk, laga och reparera', 'Reflektion och kamratrespons'],
  outro: 'Processen blir lika viktig som det färdiga föremålet.',
  spine: 'bg-[#006AA7] text-white',
  rule: 'border-white/30'
}, {
  number: 'Volym 06',
  level: 'Bedömning',
  title: 'Bedömning',
  tagline: 'Gör bedömningen enklare och rättvisare.',
  intro: 'Den sjätte volymen innehåller färdigt stöd för bedömning, betyg och omdömen i slöjd.',
  items: ['Bedömningsmatriser: material, redskap och tekniker', 'Bedömningsmatriser: arbetsprocess och värdering', 'Stöd för betyg E/C/A i åk 6 och 9', 'Observationsscheman i slöjdsalen', 'Självbedömning och formativ återkoppling', 'Teoriprov A/B med facit', '100 färdiga kommentarer till omdömen och IUP'],
  outro: 'Mindre tid på bedömning – tydligare uppföljning.',
  spine: 'bg-[#C8323C] text-white',
  rule: 'border-white/30'
}];

export const volumesIntro = {
  title: '6 digitala volymer för åk 1–9',
  text: 'Ett komplett paket i slöjd – textilslöjd och trä- och metallslöjd – som hjälper dig att planera och genomföra undervisningen under hela läsåret. Varje volym innehåller 40 sidor med lektioner, projekt, säkerhetsmaterial och bedömning.'
};

export const packItems: Array<{
  icon: LucideIcon;
  title: string;
  text: string;
}> = [{
  icon: BookOpenTextIcon,
  title: 'Lektioner minut för minut',
  text: 'Slipp börja från ett tomt dokument. 12 lektioner på 60 minuter med mål, material, säkerhet och elevblad i tre nivåer.'
}, {
  icon: ScissorsIcon,
  title: '9 projekt i textilslöjd',
  text: 'Ett projekt per årskurs – med materiallista, mått, tidsplan och steg för steg.'
}, {
  icon: HammerIcon,
  title: '9 projekt i trä och metall',
  text: 'Från träbåt och smörkniv till fågelholk och eget designprojekt – säkert och steg för steg.'
}, {
  icon: ShieldCheckIcon,
  title: 'Verktygskörkort och säkerhet',
  text: 'Körkort för 12 verktyg och maskiner, riskbedömning, skyddsutrustning och städrutiner.'
}, {
  icon: ClipboardCheckIcon,
  title: 'Bedömning E/C/A',
  text: 'Bedömningsmatriser, observationsscheman, teoriprov med facit och 100 färdiga kommentarer.'
}, {
  icon: CalendarCheckIcon,
  title: 'Läsårsplan',
  text: 'Läsårsplan för åk 1–3, 4–6 och 7–9, terminsplan vecka för vecka och rotationsschema.'
}];

// Texts for the "Titta inuti" pages = real Volym 1 pages (s11, s14, s22, s28, s32, s39)
const pageTexts: Array<{ label: string; title: string; text: string }> = [{
  label: 'Säkerhet och ordning',
  title: 'Säkerhet och ordning i slöjdsalen',
  text: 'Fem säkerhetsregler och trafikljuset: grönt – får jag göra själv, gult – efter genomgång, rött – bara med körkort eller med läraren bredvid.'
}, {
  label: 'Lärarsida · Lektion 2 (åk 1)',
  title: 'Fläta och knyta – ett flätat armband',
  text: 'Mål, material med mått, säkerhet och minut för minut för en hel lektion på 60 minuter – plus lärarens instruktion.'
}, {
  label: 'Lärarsida · Lektion 6 (åk 3)',
  title: 'Borra och slipa – en nyckelring i trä',
  text: 'Handborr, rasp och sandpapper steg för steg – med arbetsstycket fastspänt och skyddsglasögon på.'
}, {
  label: 'Lärarsida · Lektion 9 (åk 5)',
  title: 'Plåt: klippa, böja och nita – en ljusstake',
  text: 'Aluminiumplåt, popnitar och exakta mått – från märkning till färdig ljusstake på 60 minuter.'
}, {
  label: 'Lärarsida · Lektion 11 (åk 6)',
  title: 'Idé, skiss och arbetsritning – en skärbräda',
  text: 'Slöjdprocessen i praktiken: idéskisser, arbetsritning i skala 1:2 och planering i loggboken.'
}, {
  label: 'Bedömningsmatris',
  title: 'Bedömningsmatris för slöjd',
  text: 'Från ”På väg” till ”Mycket väl godtagbart / A” – material och tekniker, planering, säkerhet, dokumentation och form.'
}];

export const pageSamples: Array<{ src: string; label: string; title: string; text: string }> = pageTexts.map((page, index) => ({
  ...page,
  src: PAGE_URLS[index] || ''
}));

export const pageTypes = ['Lektioner minut för minut', 'Projekt steg för steg', 'Säkerhet och verktygskörkort', 'Bedömningsmatriser'];

export const bonuses: Array<{
  title: string;
  text: string;
  items: string[];
  benefit: string;
}> = [{
  title: 'Slöjdstart på 5 minuter',
  text: '40 korta uppstarter som får igång lektionen direkt – med facit.',
  items: ['10 ”Vilket verktyg?”', '10 materialgissningar', '10 snabbskisser', '10 ”Rätt eller fel om säkerhet”'],
  benefit: 'Starta varje slöjdlektion med fokus – utan extra förberedelse.'
}, {
  title: 'Vikariepärm för slöjd',
  text: '15 lektioner som vikarien kan genomföra utan maskiner och vassa verktyg.',
  items: ['Skiss och design', 'Materiallära', 'Handsömnad', 'Teori', 'Instruktion, elevblad och facit'],
  benefit: 'Lugn och ro även när du inte kan vara på plats.'
}, {
  title: 'Föräldrakommunikation i slöjd',
  text: 'Färdiga brev för kontakten med vårdnadshavare under hela läsåret.',
  items: ['Läsårsinformation', 'Materialinsamling för återbruk', 'Säkerhetsinformation', 'Inbjudan till utställning', 'Mall för utvecklingssamtal'],
  benefit: 'Tydlig kommunikation på några minuter.'
}, {
  title: 'Slöjdutställningen – från idé till vernissage',
  text: 'Färdig plan för vår- eller julutställningen.',
  items: ['Tidsplan', 'Skyltar', 'Elevtexter', 'Kataloglayout', 'Checklista'],
  benefit: 'Visa elevernas arbeten – utan stress.'
}, {
  title: 'Affischer för slöjdsalen',
  text: '20 affischer som gör slöjdsalen tydlig och säker.',
  items: ['Säkerhetsregler', 'Verktygens namn', 'Stygn', 'Slöjdprocessen', 'Fiberriktning', 'Ordning i salen', 'Skyddsutrustning', 'Städschema'],
  benefit: 'Skriv ut och sätt upp – direkt.'
}];

export const steps: Array<{
  title: string;
  text: string;
}> = [{
  title: 'Öppna SLÖJDKLAR™',
  text: 'Ladda ner paketet och öppna volymen du behöver – planering, textil, trä och metall, säkerhet, slöjdprocessen eller bedömning.'
}, {
  title: 'Välj lektion eller projekt',
  text: 'Hitta rätt årskurs och välj lektion, projekt eller elevblad i rätt nivå.'
}, {
  title: 'Förbered dig på 5 minuter',
  text: 'Gå igenom lärarsidan, materiallistan och säkerhetsrutan.'
}, {
  title: 'Skriv ut och slöjda',
  text: 'Skriv ut elevbladen eller visa dem digitalt – och gå in i slöjdsalen med en tydlig plan.'
}];

export const audienceItems = ['Undervisar i textilslöjd eller trä- och metallslöjd', 'Är klasslärare och har slöjd i åk 1–3', 'Vill ha ett helt läsårs material i slöjd organiserat', 'Vill ha färdiga projekt med material och mått', 'Vill ha tydliga säkerhetsregler och verktygskörkort', 'Behöver uppgifter i tre nivåer', 'Vill ha färdiga bedömningsmatriser och omdömen', 'Vill känna sig bättre förberedd inför varje lektion'];

export const timeValueTasks = ['Leta projekt med rätt svårighetsgrad', 'Räkna ut mått och materialåtgång', 'Skriva säkerhetsregler och körkort', 'Göra elevblad och loggböcker', 'Bedöma och skriva omdömen'];

export const offerRows: Array<{
  label: string;
  detail: string;
}> = [{
  label: '6 digitala volymer – 240 sidor',
  detail: 'Läsårsplan och lektioner, textilslöjd, trä- och metallslöjd, säkerhet, slöjdprocessen och bedömning'
}, {
  label: '5 bonusar utan extra kostnad',
  detail: 'Slöjdstart, vikariepärm, föräldrakommunikation, slöjdutställningen och affischer'
}, {
  label: 'För åk 1–9',
  detail: '12 lektioner minut för minut, 18 projekt steg för steg, verktygskörkort och 100 färdiga kommentarer'
}];

export const offerIncludes = ['Omedelbar digital nedladdning', 'PDF-filer att spara', 'Utskrivbara elevblad', 'Ingen fysisk produkt skickas'];

// No buyers yet: only add authentic reviews of SLÖJDKLAR. The section stays hidden while empty.
export const testimonials: Array<{
  quote: string;
  name: string;
  location: string;
}> = [];

export const faqs = [{
  q: 'Hur får jag materialet?',
  a: 'SLÖJDKLAR™ levereras digitalt. Efter köpet får du tillgång till PDF-filer som du kan ladda ner och spara. Du kan använda materialet på dator, surfplatta eller mobil och skriva ut de sidor du behöver.'
}, {
  q: 'Vad ingår i de sex volymerna?',
  a: 'Volym 1: läsårsplan och 12 lektioner minut för minut. Volym 2: textilslöjd – 9 projekt. Volym 3: trä- och metallslöjd – 9 projekt. Volym 4: säkerhet och verktyg. Volym 5: slöjdprocessen och design. Volym 6: bedömning.'
}, {
  q: 'Vilka årskurser passar det för?',
  a: 'SLÖJDKLAR™ är för åk 1–9. Du får läsårsplaner för åk 1–3, 4–6 och 7–9 och ett projekt per årskurs i både textil och trä och metall.'
}, {
  q: 'Passar det både textilslöjd och trä- och metallslöjd?',
  a: 'Ja. Paketet täcker båda materialområdena, med ett rotationsschema så att du kan planera textil och trä och metall under samma läsår.'
}, {
  q: 'Bygger materialet på Lgr22?',
  a: 'Ja. SLÖJDKLAR™ bygger på det centrala innehållet i kursplanen i slöjd i Lgr22: material, redskap och tekniker, slöjdens arbetsprocesser, estetiska och kulturella uttrycksformer samt hållbarhet. SLÖJDKLAR™ är ett fristående undervisningsmaterial.'
}, {
  q: 'Hur hanteras säkerheten?',
  a: 'Varje lektion har en SÄKERHET-ruta, och Volym 4 innehåller verktygskörkort för 12 verktyg och maskiner, riskbedömning och rutiner: skyddsglasögon vid sågning, borrning och slipning, arbetsstycket fastspänt och händerna bakom eggen. Maskiner används först efter körkort, och varmt arbete som lödning görs bara i åk 7–9 tillsammans med läraren. Följ alltid skolans egna rutiner.'
}, {
  q: 'Finns det mått och materiallistor?',
  a: 'Ja. Alla 18 projekt har materiallista, mått, tidsplan, steg-för-steg-instruktioner och vanliga fel.'
}, {
  q: 'Kan jag verkligen förbereda en lektion på 5 minuter?',
  a: 'SLÖJDKLAR™ är utformat för att göra förberedelserna snabbare. Du öppnar en volym, väljer en färdig lektion eller ett projekt och går igenom lärarsidan på ungefär fem minuter. Material behöver förstås finnas i salen, och tiden kan variera beroende på grupp.'
}, {
  q: 'Kan jag använda materialet för elever på olika nivåer?',
  a: 'Ja. Elevbladen finns i tre nivåer – stöd, standard och utmaning – så att alla elever kan arbeta mot samma mål.'
}, {
  q: 'Ingår facit?',
  a: 'Ja. Quiz, teoriprov A/B och uppstarter har facit, och paketet innehåller bedömningsmatriser, självbedömning och 100 färdiga kommentarer till omdömen.'
}, {
  q: 'Ingår de fem bonusarna?',
  a: `Ja. Alla fem bonusar ingår i paketet utan extra kostnad – allt för ${PRICE}.`
}, {
  q: 'Är det en prenumeration?',
  a: 'Nej. Du betalar en gång och får tillgång till det digitala materialet. Inga återkommande avgifter.'
}, {
  q: 'Är filerna utskrivbara?',
  a: 'Ja. Allt levereras som utskrivbara PDF-filer. Du väljer själv vilka sidor du skriver ut, och du kan även visa materialet digitalt.'
}, {
  q: 'Finns det någon garanti?',
  a: 'Ja. SLÖJDKLAR™ har 7 dagars nöjdhetsgaranti. Om materialet inte motsvarar dina förväntningar kan du kontakta oss och begära återbetalning inom 7 dagar från köpet enligt villkoren.'
}];

export const footerLinks = ['Köpvillkor', 'Integritetspolicy', 'Återbetalningspolicy', 'Kontakt'];
